import React, { useCallback, useMemo, useState } from "react";

import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { isDatasetIdentifier } from "utils/utils";
import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import DatasetMultiSelect from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import { T } from "services/lang/LanguageService";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { HighlightColor, HighlightRange } from "components/dashboard/product-template-table/utils/interfaces";
import { Instant } from "@js-joda/core";
import { toInstant } from "utils/time-utils";
import DatasetDisplay from "./DatasetDisplay";

type DatasetProps = {
  values: string | string[] | null;
  changeHandlerMulti?: (receivedValue: string[] | null) => void;
  changeHandler?: (receivedValue: string | null) => void;
  isInEditMode: boolean;
  decimalPlaces?: number;
  isSingleDataset?: boolean;
  highlightColor?: HighlightColor | null;
  bgHighlightRanges?: HighlightRange[] | null;
  maxAge?: number;
  updateFrequency?: number;
  onHighlightColors?: (color?: string, bgColor?: string, colorEntireRow?: boolean) => void;
};

const Dataset: React.FC<DatasetProps> = ({
  changeHandler,
  changeHandlerMulti,
  values,
  isInEditMode,
  decimalPlaces,
  isSingleDataset,
  maxAge,
  bgHighlightRanges,
  highlightColor,
  updateFrequency,
  onHighlightColors,
}) => {
  const getCurrentValues = (): string | string[] | null => {
    if (typeof values === "string") {
      return isSingleDataset ? values : [values];
    }
    return values;
  };

  const [dialogVisible, setDialogVisible] = useState(false);
  const [currentDatasets, setCurrentDatasets] = useState<string | string[] | null>(getCurrentValues());
  const handleDatasetsChange = useCallback((newDatasets: string[] | string | null) => {
    setCurrentDatasets(newDatasets);
  }, []);

  const handleDatasetsSave = useCallback(() => {
    if (isSingleDataset && changeHandler) {
      changeHandler(currentDatasets as string | null);
    } else if (changeHandlerMulti) {
      changeHandlerMulti(currentDatasets as string[]);
    }
    setDialogVisible(false);
  }, [changeHandler, changeHandlerMulti, isSingleDataset, currentDatasets]);

  const dialogFooterTemplate = useCallback(() => {
    return (
      <div className="flex justify-content-end gap-2 mt-3">
        <Button
          label={T("word.cancel")}
          icon="pi pi-times"
          onClick={() => setDialogVisible(false)}
          className="component-editor-button icon-red"
        />
        <Button
          type="button"
          label={T("action.save")}
          icon="pi pi-check"
          onClick={handleDatasetsSave}
          className="component-editor-button icon-green"
        />
      </div>
    );
  }, [handleDatasetsSave]);

  if (isInEditMode) {
    const buttonTooltipTemplate = () => {
      if (isSingleDataset) {
        return `${currentDatasets?.length === 0 || currentDatasets == null ? "0" : "1"} ${T("label.selected-datasets")}`;
      }
      return `${currentDatasets?.length} ${T("label.selected-datasets")}`;
    };

    const getLabel = (datasets?: string | string[] | null): string => {
      if (!datasets) return "";

      if (typeof datasets === "string") {
        return datasets.length > 0 ? "1" : "";
      }

      return datasets.length.toString();
    };

    return (
      <>
        <Button
          label={getLabel(currentDatasets)}
          className="bg-gray-200 border-none text-black-alpha-90 hover:bg-gray-300 h-2rem"
          icon="pi pi-list"
          onClick={() => setDialogVisible(true)}
          tooltipOptions={{ position: "bottom" }}
          tooltip={buttonTooltipTemplate()}
        />
        <Dialog
          header={isSingleDataset ? T("action.select-dataset") : T("action.select-datasets")}
          visible={dialogVisible}
          style={{ width: "40rem" }}
          footer={dialogFooterTemplate}
          modal
          onHide={() => setDialogVisible(false)}
        >
          {isSingleDataset ? (
            <DatasetDropdown value={currentDatasets as string} onChange={handleDatasetsChange} />
          ) : (
            <DatasetMultiSelect value={currentDatasets as string[]} onChange={handleDatasetsChange} />
          )}
        </Dialog>
      </>
    );
  }

  const getNormalizedValues = () => {
    if (Array.isArray(values)) {
      return values;
    }
    return values ? [values] : [];
  };

  const datasets = getNormalizedValues().map(value => (isDatasetIdentifier(value) ? value : null));

  if (datasets.length === 0) return "-";
  return (
    <div>
      {datasets?.map(
        dataset =>
          dataset && (
            <DatasetDisplayWrapper
              key={dataset}
              identifier={dataset}
              decimalPlaces={decimalPlaces}
              maxAge={maxAge}
              updateFrequency={updateFrequency}
              highlightColor={highlightColor}
              bgHighlightRanges={bgHighlightRanges}
              onHighlightColors={onHighlightColors}
            />
          )
      )}
    </div>
  );
};

const DatasetDisplayWrapper = ({
  identifier,
  decimalPlaces,
  maxAge,
  updateFrequency,
  highlightColor,
  bgHighlightRanges,
  onHighlightColors,
}: {
  identifier: string;
  decimalPlaces?: number;
  maxAge?: number;
  updateFrequency?: number;
  highlightColor?: HighlightColor | null;
  bgHighlightRanges?: HighlightRange[] | null;
  onHighlightColors?: (color?: string, bgColor?: string, colorEntireRow?: boolean) => void;
}) => {
  const { data, isLoading } = useGetRecentData([identifier!], {
    options: {
      enabled: !!identifier,
      refetchInterval: updateFrequency ? updateFrequency * 1000 : 0,
    },
  });

  const key = data?.[0].data?.[0]?.key;

  const datasetTime = useMemo(() => (key ? Instant.parse(key).toEpochMilli() : null), [key]);

  if (isLoading) return <div className="pi pi-spin pi-spinner" />;

  const value = data?.[0].data?.[0]?.value;
  const unit = data?.[0].unit;
  const stringValueMapper = data?.[0].stringValueMapper;
  const title = data?.[0].title;

  if (value == null && !isLoading) return <>&#8208;</>;

  return (
    <DatasetDisplay
      identifier={identifier}
      value={value}
      unit={unit}
      title={title}
      stringValueMapper={stringValueMapper}
      decimalPlaces={decimalPlaces}
      time={toInstant(datasetTime) ?? null}
      maxAge={maxAge}
      highlightColor={highlightColor}
      bgHighlightRanges={bgHighlightRanges}
      onHighlightColors={onHighlightColors}
    />
  );
};

export default Dataset;
